import React, { useEffect, useState } from 'react';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Column from '@amzn/meridian/column';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import TopOptions from './topOptions/topOptions';
import {
  generateJobMatches,
  setIsCancelModalOpen,
  setIsUsingNewExperience,
  setPageProgress,
} from 'src/store/newJmrPageSlice';
import { NewJMRPageMainBody, NewJMRPageWrapper, NewJMRPageFooter } from '../pageLayout/pageLayout';
import ResultsPageLegacy from '../resultsPage/resultsPageLegacy/resultsPageLegacy';
import JobMatchesTable from './jobMatches/jobMatchesTable';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { JmrPageProgress } from 'src/utils/jmrUtils';
import {
  areAllUsedHoursMoreThanZero,
  calculateUnusedTotalHours,
  isTotalUnusedHoursValid,
  toggleJob,
} from 'src/utils/placementSelectionUtils';
import '../resultsPage/resultsPageLegacy/resultsPageLegacy.scss';

/**
 * Wrap Header and footer around main component
 */
const ResultsPageWrapper = () => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useAppSelector((state) => state.user);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const { backgroundFormData, isLoadingGetPlacementDetails, jmrResults, isUsingNewExperience } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const [numSelected, setNumRows] = useState(0);
  const [totalSelectedHours, setTotalSelectedHours] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [actualShiftLength, setActualShiftLength] = useState(0);

  // Check criteria to determine whether to use the new experience
  const determineUsingNewExperience = () =>
    featureFlags.JOB_MODIFICATIONS &&
    allowedSites.some((site) => site.site === backgroundFormData.siteLocation && site.jobModificationsEnabled);

  const loadResults = async () => {
    try {
      const isNewResultsPgExperienceEnabled = determineUsingNewExperience();
      await dispatch(generateJobMatches(isNewResultsPgExperienceEnabled ? 'v2' : 'v1'));
      dispatch(setIsUsingNewExperience(isNewResultsPgExperienceEnabled));
      setActualShiftLength(
        backgroundFormData.cannotWorkNormalShiftHrs
          ? +backgroundFormData.limitedHoursPerDay
          : backgroundFormData.shiftLength,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Call generate job matches API matches
  useEffect(() => {
    if (backgroundFormData.alias && !isLoadingGetPlacementDetails && allowedSites.length) {
      loadResults();
    }
    return () => {
      setIsLoading(true);
    };
  }, [backgroundFormData, isLoadingGetPlacementDetails, allowedSites]);

  useEffect(() => {
    if (jmrResults.length) {
      const selectedRows = jmrResults.filter((row) => row.isChecked);
      setNumRows(selectedRows.length);
      setTotalSelectedHours(selectedRows.reduce((acc, row) => acc + (+row?.totalHours || 0), 0));
    }
  }, [jmrResults]);

  const validateSelectedJobs = () => {
    const selectedJobs = jmrResults.filter((jmrResult) => jmrResult.isChecked === true);
    const { shiftLength, limitedHoursPerDay } = backgroundFormData;
    if (!actualShiftLength || !selectedJobs.length) {
      return false;
    }
    const newTotalUnusedHours = calculateUnusedTotalHours(selectedJobs, shiftLength, +limitedHoursPerDay);
    const isEveryJobWithinRestrictions = selectedJobs
      // Filter for unmodified jobs
      .filter((s) => !s.conflicts || s.conflicts.every((c) => !c.appliedModifications.length))
      // The alloted hours for any such job must be within the maximum hours allowed
      .every((s) => s.maximumHoursAssociateCanDo === undefined || +s.totalHours <= s.maximumHoursAssociateCanDo);

    return (
      isTotalUnusedHoursValid(newTotalUnusedHours, shiftLength, +limitedHoursPerDay) &&
      areAllUsedHoursMoreThanZero(selectedJobs) &&
      isEveryJobWithinRestrictions
    );
  };

  return !isLoading ? (
    <>
      {isUsingNewExperience ? (
        <NewJMRPageWrapper>
          {/* Main Content */}
          <NewJMRPageMainBody
            xs="85%"
            md="75%"
            lg="60%"
            title="Select Job Matches"
            subtitle="Jobs are ranked according to the associates restrictions and ability to perform the job."
          >
            <TopOptions />
            <JobMatchesTable />
          </NewJMRPageMainBody>

          {/* Footer */}
          <NewJMRPageFooter
            leftSideContent={
              <Column spacing={'100'}>
                <Text>{`${numSelected} selected ∙ ${totalSelectedHours} of ${actualShiftLength} hrs${numSelected ? ':' : ''}`}</Text>
                <Row spacing={'200'} wrap="down" maxWidth={600}>
                  {jmrResults
                    .filter((r) => r.isChecked)
                    .map((r) => (
                      <Tag
                        key={r.id}
                        data-testid="newJmr__results__tag"
                        type="neutral"
                        onClose={() => toggleJob(r.id, jmrResults, dispatch)}
                      >
                        {r.name}
                      </Tag>
                    ))}
                </Row>
              </Column>
            }
          >
            <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
              Cancel
            </Button>
            <Button type="tertiary" onClick={() => dispatch(setPageProgress(JmrPageProgress.Restrictions))}>
              Previous
            </Button>
            <Button
              data-cy="new-jmr__restrictions__next"
              disabled={!validateSelectedJobs()}
              onClick={() => dispatch(setPageProgress(JmrPageProgress.Review))}
            >
              Next
            </Button>
          </NewJMRPageFooter>
        </NewJMRPageWrapper>
      ) : (
        <Column className="new-jmr-v1" alignmentHorizontal="center">
          <ResultsPageLegacy />
        </Column>
      )}
    </>
  ) : (
    <LoadingSpinner />
  );
};

export default ResultsPageWrapper;
